import {
  type Asset,
  BLOCK_CATEGORY_LABELS,
  blockSearchTerms,
  type DocumentSummary,
  getPreset,
  LIBRARY_TEMPLATES,
  type LibraryBlock,
  type LibraryItem,
  libraryTemplateSearchFields,
  type Project,
  searchList,
} from "@cva/shared";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Blocks,
  FileText,
  Film,
  FolderKanban,
  ImageIcon,
  LayoutTemplate,
  Loader2,
  Music,
  Search,
  Shapes,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { orpc } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * ⌘K: one search over files (titles and the texts in them), projects,
 * templates (own and built-in), building blocks and media. In the editor,
 * blocks and images are inserted into the open page instead of navigating.
 */

export interface EditorSearchActions {
  blocks: readonly LibraryBlock[];
  savedBlocks: readonly Extract<LibraryItem, { kind: "element" }>[];
  insertBlock: (block: LibraryBlock) => void;
  insertSaved: (item: Extract<LibraryItem, { kind: "element" }>) => void;
  /** Images and graphics; null when the media kind cannot be placed as an element. */
  insertMedia: (asset: Asset) => boolean;
  /** Editor actions found by title and keywords (e.g. „Serie erstellen“). */
  commands?: readonly EditorSearchCommand[];
}

export interface EditorSearchCommand {
  id: string;
  title: string;
  subtitle?: string;
  keywords?: readonly string[];
  icon: ReactNode;
  run: () => void;
}

interface Result {
  key: string;
  group: string;
  icon: ReactNode;
  title: string;
  subtitle?: string | undefined;
  thumbnail?: string | null | undefined;
  run: () => void;
}

const GROUP_ORDER = [
  "Aktionen",
  "Zuletzt bearbeitet",
  "Aktionen",
  "Dateien",
  "Bausteine",
  "Vorlagen",
  "Projekte",
  "Medien",
];

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function mediaIcon(kind: Asset["kind"]) {
  if (kind === "video") return <Film />;
  if (kind === "audio") return <Music />;
  if (kind === "svg") return <Shapes />;
  return <ImageIcon />;
}

/** Opens with ⌘K / Ctrl+K anywhere (also while typing – it is the search). */
export function useSearchShortcut(setOpen: (open: boolean) => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
}

export function SearchPalette({
  orgId,
  open,
  onOpenChange,
  editor,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editor?: EditorSearchActions | undefined;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed top-[12vh] left-1/2 z-50 flex max-h-[70vh] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 flex-col overflow-hidden rounded-xl bg-white shadow-2xl focus:outline-none"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Suchen</DialogPrimitive.Title>
          {open ? (
            <PaletteBody orgId={orgId} editor={editor} close={() => onOpenChange(false)} />
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function PaletteBody({
  orgId,
  editor,
  close,
}: {
  orgId: string;
  editor?: EditorSearchActions | undefined;
  close: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const text = useDebounced(query.trim(), 180);
  const searching = text.length >= 2;
  const server = useQuery({
    ...orpc.search.query.queryOptions({ input: { orgId, query: text, limit: 6 } }),
    enabled: searching,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  const recent = useQuery({
    ...orpc.documents.recent.queryOptions({ input: { orgId, limit: 6 } }),
    enabled: !searching,
  });

  const results = useMemo<Result[]>(() => {
    const out: Result[] = [];
    const go = (run: () => void) => () => {
      close();
      run();
    };
    const openDocument = (document: DocumentSummary) =>
      go(
        () =>
          void navigate({
            to: "/o/$orgId/documents/$documentId",
            params: { orgId, documentId: document.id },
          }),
      );
    if (!searching) {
      for (const document of recent.data ?? []) {
        out.push({
          key: `recent:${document.id}`,
          group: "Zuletzt bearbeitet",
          icon: document.kind === "video" ? <Film /> : <FileText />,
          title: document.title,
          subtitle: document.projectName,
          thumbnail: document.thumbnailUrl,
          run: openDocument(document),
        });
      }
      return out;
    }
    const data = server.data;
    for (const document of data?.documents ?? []) {
      out.push({
        key: `doc:${document.id}`,
        group: "Dateien",
        icon: document.kind === "video" ? <Film /> : <FileText />,
        title: document.title,
        subtitle: document.snippet ? `„${document.snippet}“` : document.projectName,
        thumbnail: document.thumbnailUrl,
        run: openDocument(document),
      });
    }
    if (editor?.commands) {
      const commands = searchList(editor.commands, text, (command) => [
        command.title,
        ...(command.keywords ?? []),
      ]).slice(0, 3);
      for (const command of commands) {
        out.push({
          key: `command:${command.id}`,
          group: "Aktionen",
          icon: command.icon,
          title: command.title,
          subtitle: command.subtitle,
          run: go(command.run),
        });
      }
    }
    if (editor) {
      const saved = searchList(editor.savedBlocks, text, (item) => [item.name]).slice(0, 4);
      for (const item of saved) {
        out.push({
          key: `saved:${item.id}`,
          group: "Bausteine",
          icon: <Blocks />,
          title: item.name,
          subtitle: "Meine Bausteine · einfügen",
          run: go(() => editor.insertSaved(item)),
        });
      }
      const blocks = searchList(editor.blocks, text, blockSearchTerms).slice(0, 6 - saved.length);
      for (const block of blocks) {
        out.push({
          key: `block:${block.id}`,
          group: "Bausteine",
          icon: <Blocks />,
          title: block.title,
          subtitle: `${BLOCK_CATEGORY_LABELS[block.category]} · einfügen`,
          run: go(() => editor.insertBlock(block)),
        });
      }
    } else {
      for (const item of data?.blocks ?? []) {
        out.push({
          key: `saved:${item.id}`,
          group: "Bausteine",
          icon: <Blocks />,
          title: item.name,
          subtitle: "Meine Bausteine",
          run: go(() =>
            toast.info(`„${item.name}“ fügst du in einer Datei ein: ⌘K oder Tab „Bausteine“.`),
          ),
        });
      }
    }
    for (const template of data?.templates ?? []) {
      out.push({
        key: `tpl:${template.id}`,
        group: "Vorlagen",
        icon: <LayoutTemplate />,
        title: template.title,
        subtitle: `Eigene Vorlage · ${getPreset(template.formatPreset)?.label ?? template.formatPreset}`,
        thumbnail: template.thumbnailUrl,
        run: go(
          () =>
            void navigate({
              to: "/o/$orgId/templates",
              params: { orgId },
              search: { use: template.id },
            }),
        ),
      });
    }
    for (const template of searchList(LIBRARY_TEMPLATES, text, libraryTemplateSearchFields).slice(
      0,
      5,
    )) {
      out.push({
        key: `lib:${template.id}`,
        group: "Vorlagen",
        icon: <LayoutTemplate />,
        title: template.title,
        subtitle: `Vorlagen-Bibliothek · ${getPreset(template.presetId)?.label ?? ""}`,
        run: go(
          () =>
            void navigate({
              to: "/o/$orgId/templates",
              params: { orgId },
              search: { library: template.id },
            }),
        ),
      });
    }
    for (const project of data?.projects ?? ([] as Project[])) {
      out.push({
        key: `project:${project.id}`,
        group: "Projekte",
        icon: <FolderKanban />,
        title: project.name,
        subtitle: `${project.documentCount} ${project.documentCount === 1 ? "Datei" : "Dateien"}${project.archivedAt ? " · archiviert" : ""}`,
        run: go(
          () =>
            void navigate({
              to: "/o/$orgId/projects/$projectId",
              params: { orgId, projectId: project.id },
            }),
        ),
      });
    }
    for (const asset of data?.media ?? []) {
      const placeable = Boolean(editor) && (asset.kind === "image" || asset.kind === "svg");
      out.push({
        key: `media:${asset.id}`,
        group: "Medien",
        icon: mediaIcon(asset.kind),
        title: asset.name,
        subtitle: placeable ? "Mediathek · einfügen" : "Mediathek",
        thumbnail: asset.thumbnailUrl,
        run: go(() => {
          if (placeable && editor?.insertMedia(asset)) return;
          void navigate({
            to: "/o/$orgId/media",
            params: { orgId },
            search: { q: asset.name, open: asset.id },
          });
        }),
      });
    }
    return out.sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
  }, [searching, recent.data, server.data, editor, text, navigate, orgId, close]);

  // A new query starts at the top (not every re-render of the list, e.g. by presence updates).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the query changes
  useEffect(() => setActive(0), [text]);
  const current = Math.min(active, Math.max(0, results.length - 1));
  // Results of the previous query are still shown while the new ones load: Enter waits.
  const stale = searching && (server.isPlaceholderData || text !== query.trim());
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${current}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [current]);

  const loading = searching && (server.isFetching || text !== query.trim());
  let lastGroup = "";
  return (
    <>
      <div className="flex items-center gap-3 border-b border-zinc-100 px-4">
        <Search className="size-5 shrink-0 text-zinc-400" />
        <input
          // biome-ignore lint/a11y/noAutofocus: the search field of a dialog opened to type in
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActive(Math.min(results.length - 1, current + 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActive(Math.max(0, current - 1));
            } else if (event.key === "Enter") {
              event.preventDefault();
              if (!stale) results[current]?.run();
            }
          }}
          placeholder={
            editor
              ? "Dateien, Bausteine, Vorlagen, Medien suchen …"
              : "Dateien, Texte, Vorlagen, Projekte, Medien suchen …"
          }
          aria-label="Suchen"
          className="h-14 min-w-0 flex-1 bg-transparent text-base text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
        />
        {loading ? <Loader2 className="size-4 shrink-0 animate-spin text-zinc-400" /> : null}
        <kbd className="hidden rounded border border-zinc-200 px-1.5 py-0.5 text-[10px] text-zinc-500 sm:block">
          esc
        </kbd>
      </div>
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-2" role="listbox">
        {results.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-zinc-500">
            {searching
              ? loading
                ? "Sucht …"
                : `Nichts gefunden für „${text}“.`
              : "Tippe, um in Dateien (auch in den Texten darin), Vorlagen, Bausteinen und Medien zu suchen."}
          </p>
        ) : (
          results.map((result, index) => {
            const header = result.group !== lastGroup ? result.group : null;
            lastGroup = result.group;
            return (
              <div key={result.key}>
                {header ? (
                  <p className="px-3 pt-3 pb-1 text-[11px] font-semibold text-zinc-700">{header}</p>
                ) : null}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === current}
                  data-index={index}
                  onMouseMove={() => setActive(index)}
                  onClick={result.run}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left",
                    index === current ? "bg-brand-50" : "hover:bg-zinc-50",
                  )}
                >
                  <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-zinc-100 text-zinc-500 [&_svg]:size-4">
                    {result.thumbnail ? (
                      <img src={result.thumbnail} alt="" className="size-full object-cover" />
                    ) : (
                      result.icon
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-zinc-900">
                      {result.title}
                    </span>
                    {result.subtitle ? (
                      <span className="block truncate text-xs text-zinc-500">
                        {result.subtitle}
                      </span>
                    ) : null}
                  </span>
                </button>
              </div>
            );
          })
        )}
      </div>
      <div className="flex items-center gap-3 border-t border-zinc-100 px-4 py-2 text-[11px] text-zinc-500">
        <span>↑↓ auswählen</span>
        <span>↵ öffnen</span>
        <span className="ml-auto">⌘K</span>
      </div>
    </>
  );
}

/** Search button for the sidebar (shows the shortcut). */
export function SearchButton({ onClick, className }: { onClick: () => void; className?: string }) {
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-sm text-zinc-500 hover:border-zinc-300 hover:bg-white",
        className,
      )}
    >
      <Search className="size-4" />
      <span className="flex-1 text-left">Suchen</span>
      <kbd className="rounded border border-zinc-200 bg-white px-1 text-[10px]">
        {mac ? "⌘K" : "Strg K"}
      </kbd>
    </button>
  );
}
